import { FC, useState } from 'react';
import CommoditiesAddTemplate from '../../templates/CommoditiesAddTemplate/CommoditiesAddTemplate';
import { useNavigate } from 'react-router-dom';
import { CommoditySaveRequestDto } from '../../../store/tradeAPI/commodity/dto/request/commodity-save-request.dto';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchCommoditiesNonRecursive,
  saveCommodity,
} from '../../../store/tradeAPI/commodity/reducers/commodity.slice';
import { getUUID } from '../../../utils/UUID';
import { NonRecursiveCommodityResponseDto } from '../../../store/tradeAPI/commodity/dto/response/non-recursive-commodity-response.dto';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import Spinner from '../../atoms/Spinner/Spinner';
import { LocalesLanguagesConstants } from '../../../locales/locales';
import { PromiseSettledResult } from '../../../utils/types/types';
import { CommodityResponseDto } from '../../../store/tradeAPI/commodity/dto/response/commodity-response.dto';

interface CommoditiesAddPageProps {}

const CommoditiesAddPage: FC<CommoditiesAddPageProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [commodity, setCommodity] = useState<NonRecursiveCommodityResponseDto>({
    name: '',
    parentId: '',
    hsCode: '',
    qtyUnit: '',
    timeUnit: '',
    image: '',
    id: getUUID(),
    versionId: 0,
    createdByUserId: '',
    createdOnDateTimeUtc: '',
    language: '',
    published: true,
  });

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const generateAllTranslations: () => Promise<
    PromiseSettledResult<void>[]
  > = async () => {
    const rejectedResults: PromiseSettledResult<void>[] = [
      {
        status: 'rejected',
        reason: new Error(
          'Must save verification status before generating translation'
        ),
      },
    ];

    return Promise.reject(rejectedResults);
  };

  const generateTranslation = async () => {};

  const [imageMeta, setImageMeta] = useState<ImageMeta>();
  const { status } = useSelector((state: RootState) => state.commoditySlice);

  const handleSubmit = async (commodity: NonRecursiveCommodityResponseDto) => {
    let image: MediaSaveResponseDto = {
      href: '',
    };
    if (commodity.image && commodity.image !== '' && imageMeta) {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([commodity.image], {
          type: imageMeta?.imageType ?? 'image/png',
        }),
        imageMeta?.imageSrc
      );
      image = (await dispatch(saveMedia(formData)))
        .payload as MediaSaveResponseDto;
    }
    const commodityToSave: CommoditySaveRequestDto = {
      name: commodity.name,
      parentId: commodity.parentId,
      hsCode: commodity.hsCode,
      qtyUnit: commodity.qtyUnit,
      timeUnit: commodity.timeUnit,
      image: image.href,
      id: commodity.id,
      published: commodity.published,
    };

    const response = await dispatch(
      saveCommodity({
        saveRequest: commodityToSave,
        language: selectedLanguage,
      })
    );
    const newCommodity = response.payload as CommodityResponseDto;

    dispatch(fetchCommoditiesNonRecursive());
    navigate(`/commodities/edit/${newCommodity.id}`);
  };

  const handleOnChange = (commodity: NonRecursiveCommodityResponseDto) => {
    setCommodity(commodity);
  };

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  if (status === 'loading') {
    return <Spinner />;
  }

  return (
    <CommoditiesAddTemplate
      handleSubmit={handleSubmit}
      commodity={commodity}
      handleOnChange={handleOnChange}
      handleImageOnChange={handleImageOnChange}
      setLanguage={setLanguage}
      selectedLanguage={selectedLanguage}
      fromLanguage={fromLanguage}
      setFromLanguage={setFromLanguage}
      toLanguage={toLanguage}
      setToLanguage={setToLanguage}
      generateTranslation={generateTranslation}
      generateAllTranslations={generateAllTranslations}
    />
  );
};

export default CommoditiesAddPage;
